import { useState, useCallback, useMemo } from 'react';
import { Check, FlaskConical, Droplets } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { SOLUBILITY_DATA, CATIONS } from './SolubilityGrid';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Testing Phosphate Solubility',
    content: 'Phosphates (PO4 3-) are generally insoluble, except for Group 1 (Na+, K+) and ammonium (NH4+) phosphates. The phosphate ion forms ionic compounds with most metals that have very low Ksp values.',
    whatHappens: 'Adding phosphate solution to cation solutions tests whether insoluble metal phosphates form.',
    observe: ['Most phosphates form white precipitates', 'Group 1 phosphates are soluble', 'Phosphate is a tribasic anion (PO4 3-)'],
    equation: '3M(n+) + nPO4(3-) -> M3(PO4)n (for insoluble combinations)'
  },
  2: {
    title: 'Testing Hydroxide Solubility',
    content: 'Most metal hydroxides are insoluble, with exceptions being Group 1 hydroxides and Ba(OH)2. Metal hydroxides often show characteristic colors due to d-orbital transitions in transition metals.',
    whatHappens: 'Adding hydroxide solution reveals the characteristic colors of metal hydroxide precipitates.',
    observe: ['Cu(OH)2: light blue', 'Fe(OH)3: rust/orange', 'Ni(OH)2: pale green', 'Co(OH)2: rose-blue'],
    equation: 'M(n+) + nOH(-) -> M(OH)n (precipitate)'
  },
  3: {
    title: 'Recording Color Observations',
    content: 'The colors of metal hydroxide precipitates are diagnostically useful. Transition metal hydroxides display colors related to d-orbital electron configurations, while main group metal hydroxides are typically white.',
    whatHappens: 'Recording the distinctive colors allows identification of unknown metal ions in qualitative analysis.',
    observe: ['Transition metals show colors', 'Main group metals are white/colorless', 'Color relates to electronic structure']
  }
};

export default function Part8PhosphateHydroxide() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [phosphateTested, setPhosphateTested] = useState<Set<string>>(new Set());
  const [hydroxideTested, setHydroxideTested] = useState<Set<string>>(new Set());
  const [currentAnion, setCurrentAnion] = useState<'PO43-' | 'OH-'>('PO43-');
  const [resultsRecorded, setResultsRecorded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const allPhosphateDone = phosphateTested.size === CATIONS.length;
  const allHydroxideDone = hydroxideTested.size === CATIONS.length;
  const completedSteps = [allPhosphateDone, allHydroxideDone, resultsRecorded].filter(Boolean).length;

  const handleTestCation = useCallback((cation: string) => {
    if (currentAnion === 'PO43-') {
      if (!phosphateTested.has(cation)) {
        setPhosphateTested(prev => {
          const newSet = new Set(prev);
          newSet.add(cation);
          if (newSet.size === CATIONS.length) {
            setCurrentStep(2);
            setCurrentAnion('OH-');
          }
          return newSet;
        });
      }
    } else {
      if (!hydroxideTested.has(cation)) {
        setHydroxideTested(prev => {
          const newSet = new Set(prev);
          newSet.add(cation);
          if (newSet.size === CATIONS.length) {
            setCurrentStep(3);
          }
          return newSet;
        });
      }
    }
  }, [currentAnion, phosphateTested, hydroxideTested]);

  const handleRecordResults = useCallback(() => {
    if (allPhosphateDone && allHydroxideDone && !resultsRecorded) {
      setResultsRecorded(true);
    }
  }, [allPhosphateDone, allHydroxideDone, resultsRecorded]);

  // Get hydroxide precipitates (colorful ones)
  const hydroxidePrecipitates = useMemo(() => {
    return CATIONS.map(cation => {
      const data = SOLUBILITY_DATA[cation]?.['OH-'];
      return {
        cation,
        ...data
      };
    }).filter(d => d && !d.soluble);
  }, []);

  const testedSet = currentAnion === 'PO43-' ? phosphateTested : hydroxideTested;
  const totalTested = currentAnion === 'PO43-' ? phosphateTested.size : hydroxideTested.size;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part VIII: Phosphate & Hydroxide</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Test Phosphate', hint: 'PO43- with all cations', done: allPhosphateDone },
            { id: 2, title: 'Test Hydroxide', hint: 'OH- with all cations', done: allHydroxideDone },
            { id: 3, title: 'Record Results', hint: 'Note colored precipitates', done: resultsRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Info Panels */}
        <div className="info-panel">
          {currentAnion === 'PO43-' ? (
            <>
              <h4>Phosphate Solubility</h4>
              <p>Most phosphates are insoluble except for Group 1 and ammonium phosphates. Expect white precipitates for most transition metal phosphates.</p>
            </>
          ) : (
            <>
              <h4>Metal Hydroxides</h4>
              <p>Hydroxide precipitates show characteristic colors:</p>
              <ul className="color-list">
                <li><span className="dot" style={{background: '#6495ED'}}></span>Cu(OH)2 - Blue</li>
                <li><span className="dot" style={{background: '#98FB98'}}></span>Ni(OH)2 - Green</li>
                <li><span className="dot" style={{background: '#DDA0DD'}}></span>Co(OH)2 - Rose/Blue</li>
                <li><span className="dot" style={{background: '#B7410E'}}></span>Fe(OH)3 - Rust</li>
              </ul>
            </>
          )}
        </div>
      </div>

      <LabBench
        experimentName="Phosphate & Hydroxide Testing"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['0.3 M H₃PO₄ solution', '0.1 M NaOH solution', 'Cation solutions']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="testing-workspace">
          {/* Current Anion Indicator */}
          <div className="anion-indicator">
            <div className={`anion-tab ${currentAnion === 'PO43-' ? 'active' : ''}`}>
              PO<sub>4</sub><sup>3-</sup> Phosphate
              {allPhosphateDone && <Check size={14} className="check-icon" />}
            </div>
            <div className={`anion-tab ${currentAnion === 'OH-' ? 'active' : ''}`}>
              OH<sup>-</sup> Hydroxide
              {allHydroxideDone && <Check size={14} className="check-icon" />}
            </div>
          </div>

          {/* Reagent Bottles */}
          <div className="reagent-row">
            <div className={`reagent-bottle ${currentAnion === 'PO43-' ? 'active' : ''}`}>
              <svg viewBox="0 0 50 90" width={55} height={95}>
                <rect x="12" y="25" width="26" height="55" rx="3" fill="#5A5A5A" />
                <rect x="15" y="30" width="20" height="45" fill="#F5F5F5" opacity="0.6" />
                <rect x="17" y="10" width="16" height="18" rx="2" fill="#333" />
                <text x="25" y="55" fontSize="6" fill="#333" textAnchor="middle">H₃PO₄</text>
              </svg>
              <span>0.3 M Phosphate</span>
            </div>
            <div className={`reagent-bottle ${currentAnion === 'OH-' ? 'active' : ''}`}>
              <svg viewBox="0 0 50 90" width={55} height={95}>
                <rect x="12" y="25" width="26" height="55" rx="3" fill="#5A5A5A" />
                <rect x="15" y="30" width="20" height="45" fill="#E3F2FD" opacity="0.6" />
                <rect x="17" y="10" width="16" height="18" rx="2" fill="#333" />
                <text x="25" y="55" fontSize="6" fill="#333" textAnchor="middle">NaOH</text>
              </svg>
              <span>0.1 M Hydroxide</span>
            </div>
          </div>

          {/* Cation Testing Grid */}
          <div className="cation-grid">
            {CATIONS.map(cation => {
              const isTested = testedSet.has(cation);
              const data = SOLUBILITY_DATA[cation]?.[currentAnion];
              const isPrecipitate = data && !data.soluble;

              return (
                <div
                  key={cation}
                  className={`cation-cell ${isTested ? 'tested' : 'untested'} ${!isTested ? 'clickable' : ''}`}
                  onClick={() => !isTested && handleTestCation(cation)}
                >
                  <div
                    className="cell-visual"
                    style={{
                      backgroundColor: isTested && isPrecipitate && data?.color ? data.color : (isTested ? '#E8F5E9' : '#F5F5F5')
                    }}
                  >
                    {isTested && (
                      <span className={`result ${isPrecipitate ? 'ppt' : 'sol'}`}>
                        {isPrecipitate ? 'ppt' : 'S'}
                      </span>
                    )}
                  </div>
                  <span className="cation-name">{cation}</span>
                  {isTested && isPrecipitate && data?.description && (
                    <span className="desc-tooltip">{data.description}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress */}
          <div className="progress-info">
            Testing {currentAnion}: {totalTested} / {CATIONS.length} complete
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {!allPhosphateDone && currentAnion === 'PO43-' && (
              <div className="status-message info">
                <Droplets size={20} />
                Click cations to test with phosphate solution
              </div>
            )}
            {allPhosphateDone && !allHydroxideDone && currentAnion === 'OH-' && (
              <div className="status-message info">
                <Droplets size={20} />
                Now test with hydroxide solution - watch for colored precipitates!
              </div>
            )}
            {allPhosphateDone && allHydroxideDone && !resultsRecorded && (
              <div className="status-message info" onClick={handleRecordResults} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click to record all results
              </div>
            )}
            {resultsRecorded && (
              <div className="status-message success">
                <Check size={20} />
                Part VIII complete! Note the characteristic hydroxide colors.
              </div>
            )}
          </div>

          {/* Hydroxide Colors Summary */}
          {resultsRecorded && (
            <div className="hydroxide-summary">
              <h4>Metal Hydroxide Colors</h4>
              <div className="color-grid">
                {hydroxidePrecipitates.map(p => (
                  <div key={p.cation} className="color-item">
                    <div className="color-swatch" style={{ backgroundColor: p.color || '#DDD' }} />
                    <div className="color-info">
                      <span className="formula">{p.precipitate}</span>
                      <span className="desc">{p.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <style>{`
          .testing-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .anion-indicator {
            display: flex;
            gap: 10px;
          }

          .anion-tab {
            padding: 10px 20px;
            background: var(--bg-surface);
            border-radius: 8px;
            font-size: 14px;
            color: var(--text-secondary);
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .anion-tab.active {
            background: var(--secondary);
            color: white;
          }

          .check-icon {
            color: white;
          }

          .reagent-row {
            display: flex;
            gap: 30px;
          }

          .reagent-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            opacity: 0.5;
            transition: all 0.2s;
          }

          .reagent-bottle.active {
            opacity: 1;
            transform: scale(1.1);
          }

          .reagent-bottle span {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .cation-grid {
            display: grid;
            grid-template-columns: repeat(6, 1fr);
            gap: 8px;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            padding: 20px;
            border-radius: 10px;
          }

          .cation-cell {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            position: relative;
          }

          .cation-cell.clickable {
            cursor: pointer;
          }

          .cation-cell.clickable:hover .cell-visual {
            transform: scale(1.1);
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          }

          .cell-visual {
            width: 45px;
            height: 45px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid rgba(0,0,0,0.2);
            transition: all 0.2s;
          }

          .result {
            font-weight: 600;
            font-size: 12px;
          }

          .result.ppt {
            color: #333;
          }

          .result.sol {
            color: var(--secondary);
          }

          .cation-name {
            font-size: 9px;
            color: #CCC;
          }

          .desc-tooltip {
            position: absolute;
            bottom: -20px;
            font-size: 8px;
            color: #999;
            white-space: nowrap;
            display: none;
          }

          .cation-cell:hover .desc-tooltip {
            display: block;
          }

          .progress-info {
            font-size: 13px;
            color: var(--text-secondary);
          }

          .info-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .info-panel h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .info-panel p {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .color-list {
            list-style: none;
            padding: 0;
            margin: 8px 0 0 0;
          }

          .color-list li {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            margin: 4px 0;
          }

          .dot {
            width: 12px;
            height: 12px;
            border-radius: 2px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .hydroxide-summary {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .hydroxide-summary h4 {
            margin: 0 0 12px 0;
            font-size: 14px;
            color: var(--secondary);
          }

          .color-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .color-item {
            display: flex;
            gap: 10px;
            align-items: center;
          }

          .color-swatch {
            width: 30px;
            height: 30px;
            border-radius: 4px;
            border: 1px solid var(--border);
          }

          .color-info {
            display: flex;
            flex-direction: column;
          }

          .color-info .formula {
            font-size: 12px;
            font-weight: 600;
          }

          .color-info .desc {
            font-size: 10px;
            color: var(--text-secondary);
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp7"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
